import { z } from 'zod';
import { BotCategory, BotId, BotPattern } from './bots.js';
/**
 * Bot blocks and rate limits (knowledge doc section 12, "Web traffic controls";
 * section 11's "Blocked bots" and "Bot rate limits" rows; P6.6).
 *
 * They are **settings** — desired state in the global, group or server layer — so
 * a new server gets them too. In a layer they name catalog bots and categories;
 * the agent turns them into literal user-agent patterns with the bot catalog
 * (`resolveBotRules`), and the root helper writes those into Nginx or Apache
 * escaped, never as regular expressions (rule 18).
 *
 * **Allowed bots win.** A bot in `allowed_bots` is never blocked or rate-limited,
 * whichever layer or rule would have: its patterns come first in what the helper
 * writes, and the first match decides.
 *
 * Every list adds across layers (`LIST_MERGE`): a block is a protection in force,
 * and a narrower scope must not quietly lift one a wider scope set. Rate limits
 * are keyed by what they match, so a server's limit for SEO tools replaces the
 * group's for the same category.
 */
const CATEGORY_MATCH = BotCategory.options.join('|');
/** `bot:<catalog id>` or `category:<bot category>`: one bot, or every bot of a kind. */
export const RateLimitMatch = z
    .string()
    .max(80)
    .regex(new RegExp(`^(bot:[a-z0-9][a-z0-9_-]{0,63}|category:(${CATEGORY_MATCH}))$`), 'bot:<catalog id> or category:<bot category>');
export const RateLimitRule = z
    .object({
    match: RateLimitMatch,
    /** Requests a minute from one address; Nginx answers the rest 429. */
    per_minute: z.number().int().min(1).max(6000),
    /** Requests over the rate let through at once before refusing starts. */
    burst: z.number().int().min(0).max(1000),
})
    .strict();
export const WEB_RULE_CAPS = {
    blockedBots: 200,
    blockedPatterns: 100,
    allowedBots: 200,
    rateLimits: 20,
};
/**
 * The rule fields of `web` in a layer and a document. All optional and never
 * defaulted, so a server without rules has the same document (and hash) it had
 * before P6.6.
 */
export const WebRuleFields = {
    /** Catalog bots answered 403. */
    blocked_bots: z.array(BotId).max(WEB_RULE_CAPS.blockedBots).optional(),
    /** Section 12's custom literal patterns, for a client the catalog does not name. */
    blocked_patterns: z.array(BotPattern).max(WEB_RULE_CAPS.blockedPatterns).optional(),
    /** Never blocked, never rate-limited. */
    allowed_bots: z.array(BotId).max(WEB_RULE_CAPS.allowedBots).optional(),
    rate_limits: z.array(RateLimitRule).max(WEB_RULE_CAPS.rateLimits).optional(),
};
/** What the agent's `web_env` module is handed to enforce. */
export const WebRules = z.object(WebRuleFields).strict();
export const hasRules = (rules) => (rules.blocked_bots?.length ?? 0) +
    (rules.blocked_patterns?.length ?? 0) +
    (rules.rate_limits?.length ?? 0) >
    0;
export function rateLimitTarget(match) {
    const [kind, value = ''] = match.split(':');
    if (kind === 'bot')
        return { kind: 'bot', id: value };
    return { kind: 'category', category: BotCategory.parse(value) };
}
/**
 * The Nginx zone a rule counts in: from what it matches, so reordering the rules
 * never gives a zone another rule's rate.
 */
export const zoneNameFor = (match) => `bz_rl_${match.replace(/[^a-z0-9]/g, '_')}`;
/** One spelling per pattern: Nginx's `~*` and Apache's `NoCase` ignore case anyway. */
function distinct(patterns) {
    const seen = new Map();
    for (const pattern of patterns) {
        const key = pattern.toLowerCase();
        if (!seen.has(key))
            seen.set(key, pattern);
    }
    return [...seen.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
/**
 * The patterns the helper writes, from rules and a catalog. Allowed bots are left
 * out of every category limit here, and come first in what is written, so a
 * blocked pattern that happens to match an allowed bot's user agent does not
 * catch it either.
 */
export function resolveBotRules(rules, catalog) {
    const byId = new Map(catalog.bots.map((bot) => [bot.id, bot]));
    const unknown = new Set();
    const entry = (id) => {
        const found = byId.get(id);
        if (found === undefined)
            unknown.add(id);
        return found;
    };
    const allowedIds = new Set(rules.allowed_bots ?? []);
    const allowed = distinct([...allowedIds].flatMap((id) => entry(id)?.ua_patterns ?? []));
    const allowedKeys = new Set(allowed.map((pattern) => pattern.toLowerCase()));
    const notAllowed = (pattern) => !allowedKeys.has(pattern.toLowerCase());
    const blocked = distinct([
        ...(rules.blocked_bots ?? [])
            .filter((id) => !allowedIds.has(id))
            .flatMap((id) => entry(id)?.ua_patterns ?? []),
        ...(rules.blocked_patterns ?? []),
    ]).filter(notAllowed);
    const limits = [];
    for (const rule of rules.rate_limits ?? []) {
        const target = rateLimitTarget(rule.match);
        const bots = target.kind === 'bot'
            ? [entry(target.id)].filter((bot) => bot !== undefined)
            : catalog.bots.filter((bot) => bot.category === target.category);
        const patterns = distinct(bots.filter((bot) => !allowedIds.has(bot.id)).flatMap((bot) => bot.ua_patterns)).filter(notAllowed);
        if (patterns.length === 0)
            continue;
        limits.push({
            zone: zoneNameFor(rule.match),
            patterns,
            per_minute: rule.per_minute,
            burst: rule.burst,
        });
    }
    limits.sort((a, b) => a.zone.localeCompare(b.zone));
    return { blocked, allowed, limits, unknown: [...unknown].sort() };
}
/**
 * Section 12's "verified search engine": one whose claims can be checked, so that
 * blocking it is a decision about real search traffic, not about impostors.
 */
export const isVerifiedSearchEngine = (bot) => bot.category === 'search' && bot.verify !== undefined;
/** Whether a literal pattern would catch this bot's user agent, as far as its patterns say. */
function catches(pattern, bot) {
    const wanted = pattern.toLowerCase();
    return bot.ua_patterns.some((own) => own.toLowerCase().includes(wanted));
}
/**
 * The catalog bots these rules block or rate-limit — including those a custom
 * pattern would catch (`bot` catches Googlebot) — less the allowed ones.
 */
export function botsTouched(rules, catalog) {
    const allowed = new Set(rules.allowed_bots ?? []);
    const named = new Set(rules.blocked_bots ?? []);
    const patterns = rules.blocked_patterns ?? [];
    const blocked = catalog.bots.filter((bot) => !allowed.has(bot.id) &&
        (named.has(bot.id) || patterns.some((pattern) => catches(pattern, bot))));
    const limited = catalog.bots.filter((bot) => !allowed.has(bot.id) &&
        (rules.rate_limits ?? []).some((rule) => {
            const target = rateLimitTarget(rule.match);
            return target.kind === 'bot' ? target.id === bot.id : target.category === bot.category;
        }));
    return { blocked, limited };
}
/**
 * One change from the Traffic page (section 12's "Web traffic controls"): each
 * button changes one layer's rules by one entry, rather than sending the whole
 * list back — two operators blocking two bots at once must both win.
 */
export const WebRuleChange = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('block'), bot: BotId }).strict(),
    z.object({ kind: z.literal('unblock'), bot: BotId }).strict(),
    z.object({ kind: z.literal('block_pattern'), pattern: BotPattern }).strict(),
    z.object({ kind: z.literal('unblock_pattern'), pattern: BotPattern }).strict(),
    /** Never blocked or rate-limited; takes the bot out of this layer's blocks and limits. */
    z.object({ kind: z.literal('allow'), bot: BotId }).strict(),
    z.object({ kind: z.literal('disallow'), bot: BotId }).strict(),
    z.object({ kind: z.literal('rate_limit'), rule: RateLimitRule }).strict(),
    z.object({ kind: z.literal('remove_rate_limit'), match: RateLimitMatch }).strict(),
]);
const withEntry = (list, entry) => [...new Set([...(list ?? []), entry])].sort();
const withoutEntry = (list, entry) => (list ?? []).filter((each) => each !== entry);
/** Rules without empty lists, so a layer that had none keeps having none. */
export function tidyRules(rules) {
    const out = {};
    if (rules.blocked_bots !== undefined && rules.blocked_bots.length > 0) {
        out.blocked_bots = rules.blocked_bots;
    }
    if (rules.blocked_patterns !== undefined && rules.blocked_patterns.length > 0) {
        out.blocked_patterns = rules.blocked_patterns;
    }
    if (rules.allowed_bots !== undefined && rules.allowed_bots.length > 0) {
        out.allowed_bots = rules.allowed_bots;
    }
    if (rules.rate_limits !== undefined && rules.rate_limits.length > 0) {
        out.rate_limits = rules.rate_limits;
    }
    return out;
}
/** One layer's rules after one change. */
export function applyRuleChange(rules, change) {
    const next = { ...rules };
    switch (change.kind) {
        case 'block':
            next.blocked_bots = withEntry(rules.blocked_bots, change.bot);
            next.allowed_bots = withoutEntry(rules.allowed_bots, change.bot);
            break;
        case 'unblock':
            next.blocked_bots = withoutEntry(rules.blocked_bots, change.bot);
            break;
        case 'block_pattern':
            next.blocked_patterns = withEntry(rules.blocked_patterns, change.pattern);
            break;
        case 'unblock_pattern':
            next.blocked_patterns = withoutEntry(rules.blocked_patterns, change.pattern);
            break;
        case 'allow':
            next.allowed_bots = withEntry(rules.allowed_bots, change.bot);
            next.blocked_bots = withoutEntry(rules.blocked_bots, change.bot);
            next.rate_limits = (rules.rate_limits ?? []).filter((rule) => rule.match !== `bot:${change.bot}`);
            break;
        case 'disallow':
            next.allowed_bots = withoutEntry(rules.allowed_bots, change.bot);
            break;
        case 'rate_limit':
            next.rate_limits = [
                ...(rules.rate_limits ?? []).filter((rule) => rule.match !== change.rule.match),
                change.rule,
            ].sort((a, b) => a.match.localeCompare(b.match));
            break;
        case 'remove_rate_limit':
            next.rate_limits = (rules.rate_limits ?? []).filter((rule) => rule.match !== change.match);
            break;
    }
    return tidyRules(next);
}
/**
 * The rules of several layers together, as the API's merge combines them
 * (`LIST_MERGE`): every list adds, and a later layer's limit for the same bot or
 * category wins.
 */
export function combineRules(layers) {
    const union = (pick) => [...new Set(layers.flatMap((rules) => pick(rules) ?? []))].sort();
    const limits = new Map();
    for (const rules of layers)
        for (const rule of rules.rate_limits ?? [])
            limits.set(rule.match, rule);
    return tidyRules({
        blocked_bots: union((rules) => rules.blocked_bots),
        blocked_patterns: union((rules) => rules.blocked_patterns),
        allowed_bots: union((rules) => rules.allowed_bots),
        rate_limits: [...limits.values()].sort((a, b) => a.match.localeCompare(b.match)),
    });
}
/** The rule fields of `web`, and nothing else of it. */
export function rulesOf(web) {
    if (web === undefined)
        return {};
    const { environment: _environment, block_rules: _blockRules, ...rules } = web;
    return tidyRules(rules);
}
/**
 * Section 12's High row: the verified search engines these rules block or
 * rate-limit that the rules before did not.
 */
export function newlyTouchedSearchEngines(before, after, catalog) {
    const touched = (rules) => {
        const { blocked, limited } = botsTouched(rules, catalog);
        return new Set([...blocked, ...limited].filter(isVerifiedSearchEngine).map((bot) => bot.id));
    };
    const was = touched(before);
    const now = touched(after);
    return catalog.bots.filter((bot) => now.has(bot.id) && !was.has(bot.id));
}
/**
 * What a person types to confirm a change that reaches a verified search engine:
 * the bot's name, the category, or the pattern — the thing they are acting on.
 */
export function confirmationFor(change, catalog) {
    const name = (id) => catalog.bots.find((bot) => bot.id === id)?.name ?? id;
    switch (change.kind) {
        case 'block':
        case 'unblock':
        case 'allow':
        case 'disallow':
            return name(change.bot);
        case 'block_pattern':
        case 'unblock_pattern':
            return change.pattern;
        case 'rate_limit': {
            const target = rateLimitTarget(change.rule.match);
            return target.kind === 'bot' ? name(target.id) : target.category;
        }
        case 'remove_rate_limit': {
            const target = rateLimitTarget(change.match);
            return target.kind === 'bot' ? name(target.id) : target.category;
        }
    }
}
/** Catalog ids the rules name that the catalog does not have. */
export function unknownBots(rules, catalog) {
    const known = new Set(catalog.bots.map((bot) => bot.id));
    const named = [
        ...(rules.blocked_bots ?? []),
        ...(rules.allowed_bots ?? []),
        ...(rules.rate_limits ?? []).flatMap((rule) => {
            const target = rateLimitTarget(rule.match);
            return target.kind === 'bot' ? [target.id] : [];
        }),
    ];
    return [...new Set(named.filter((id) => !known.has(id)))].sort();
}
//# sourceMappingURL=web-rules.js.map